import { LabelKeys } from 'src/app/models/enums/label-keys';
import {
  ButtonConfig,
  DimensionType,
  ErrorTranslations,
} from 'src/app/models/forms.model';
import { LabelInfo, TranslationInfo } from 'src/app/models/label-info.model';

export class DependentDictionaryData {
  isDictionaryDependent: boolean;
  dictionaryControlIdentifier: string;
  dependentValueIds: number[];
  configuringType?: 'step' | 'section' | 'component';
  configuringId?: string;
}

export interface TranslationItem {
  languageId: number;
  languageIso: string;
  value: string;
}

export class SectionData {
  identifier: string;
  title: string;
  type: number;
  controls: ControlData[];
  presentationControls?: PresentationControl[];
  isNavigationSection?: boolean;
  canDepend?: boolean;
  translations?: LabelInfo[];

  // UI Additions (optional)
  collapsed?: boolean;
  showSectionTitle?: boolean;
  showSectionSeparator?: boolean;
  dependentDictionary?: DependentDictionaryData;

  //display mode
  titleStyle?: string;

  // Layout margins
  marginTop?: string;
  marginBottom?: string;
  firstRowMarginTop?: string;
  firstRowMarginBottom?: string;
}

export class BaseControl {
  title: string;
  identifier: string;
  translations: LabelInfo[];
  dimension: number;
  dimensionType: DimensionType;
  autoDimension: boolean;
  canEdit: boolean = true;
  specificControlData: any;
  isPresentationControl: boolean = false;
}

export enum StepControlType {
  ManualInput = 0,
  Paragraph = 1,
  CheckBox = 2,
  Dictionary = 3,
  Buttons = 4,
  FluxInfo = 5,
  NewRow = 6,
  Metadata = 7,
  ApiMethodField = 8,
  DownloadDocuments = 9,
  ViewDocuments = 10,
  UploadDocuments = 11,
}
export enum PresentationControlType {
  FluxComponent = 0,
  Download = 1,
  Paragraph = 2,
  View = 3,
  NewRow = 4,
  Status = 5,
  ApiMethod = 6,
}

export class ControlData extends BaseControl {
  type: StepControlType;
  dependentDictionary: DependentDictionaryData;
  canDepend: boolean = false;
  isOnlyForConfig: boolean = false;
  isMandatory: boolean;
  errorsTranslations: ErrorTranslations[];
}

export class PresentationControl extends BaseControl {
  type: PresentationControlType;
  collectionControlIdentifier?: string | undefined;
  apiMethodId?: string | undefined;
  apiMethodFieldId?: string | undefined;
}

export interface StepData {
  // V1 properties
  identifier: string;
  name: string;
  type: number;
  sections: SectionData[];
  isVisible?: boolean;
  config?: any;
  translations: TranslationInfo[];
  panelOpenState?: boolean;
  buttons: ButtonConfig[];
  dependentDictionary?: DependentDictionaryData;
  errorsTranslations: ErrorTranslations[];
  hasNameAndDescription?: boolean;
  canDepend?: boolean;
  collapsed?: boolean;
}

export const ComponentTypeDetails: Record<
  string,
  { icon: string; label: string }
> = {
  ManualInput: {
    icon: 'input',
    label: LabelKeys.appConfigEdit.flux.section.manualInput,
  }, // 0
  Paragraph: {
    icon: 'segment',
    label: LabelKeys.appConfigEdit.flux.section.paragraph,
  }, // 1
  Checkbox: {
    icon: 'check_box',
    label: LabelKeys.appConfigEdit.flux.section.checkbox,
  }, // 2
  Dictionary: {
    icon: 'data_object',
    label: LabelKeys.appConfigEdit.flux.section.dictionaryComponent,
  }, // 3
  Buttons: {
    icon: 'smart_button',
    label: LabelKeys.appConfigEdit.flux.section.buttons,
  }, // 4
  NewRow: {
    icon: 'height',
    label: LabelKeys.appConfigEdit.flux.section.newRow,
  }, // 5
  // FluxComponent: { icon: 'dynamic_feed', label: LabelKeys.appConfigEdit.flux.section.fluxInfo }, // PresentationControlType 0 // TODO: Verify this key if needed, wasn't explicitly found
  Download: {
    icon: 'file_download',
    label: LabelKeys.appConfigEdit.flux.section.presentationDownloadComponent,
  }, // PresentationControlType 1
  Upload: {
    icon: 'file_upload',
    label: LabelKeys.flux.uploadDocument,
  }, // PresentationControlType 2
  View: { icon: 'visibility', label: LabelKeys.flux.viewDocument }, // PresentationControlType 3
  FluxComponent: { icon: 'visibility', label: LabelKeys.common.actions.view },
  ApiMethodField: { icon: 'visibility', label: 'appConfigEdit.partners.generalSettings.apiMethod' },
};

export const StepTypeDetails: Record<string, { icon: string; label: string }> =
{
  Custom: { icon: 'add_circle_outline', label: LabelKeys.flux.customStep }, // 5
  GenerateDocument: {
    icon: 'description',
    label: LabelKeys.flux.generateDocument,
  }, // 8
  Action: { icon: 'directions_run', label: LabelKeys.flux.action }, // 6
  ViewOffer: { icon: 'visibility', label: LabelKeys.flux.viewOffer }, // 9
  Sign: { icon: 'draw', label: LabelKeys.flux.sign }, // 11
  Email: { icon: 'email', label: LabelKeys.flux.verifyEmail }, // 12
  Phone: { icon: 'phone', label: LabelKeys.flux.verifyPhone }, // 13
  IdScan: { icon: 'badge', label: LabelKeys.flux.idScan }, // 15
  Kyc: { icon: 'person_search', label: LabelKeys.flux.identification }, // 16
  Status: { icon: 'check_circle_outline', label: LabelKeys.flux.status }, // 17
  Final: { icon: 'flag', label: LabelKeys.flux.finalStep }, // 18
  Pause: { icon: 'pause_circle_outline', label: LabelKeys.flux.pause }, // 19
  EUPLATESC: { icon: 'credit_card', label: LabelKeys.flux.euPlatesc }, // 20
  NETOPIA: { icon: 'payment', label: LabelKeys.flux.netopia }, // 21
  OpenBanking: { icon: 'account_balance', label: LabelKeys.flux.openBanking }, // 22
  Validation: { icon: 'rule', label: LabelKeys.flux.validation }, // 23
  OmniCheckPerson: { icon: 'manage_search', label: LabelKeys.flux.omniCheckPerson }, // 24
  OmniCheckBusiness: { icon: 'business_center', label: LabelKeys.flux.omniCheckBusiness }, // 25
};

export const SectionTypeDetails: Record<
  string,
  { label: string; icon: string }
> = {
  Presentation: { label: LabelKeys.flux.presentationData, icon: 'view_list' }, // 1 - Swapped order to match image roughly
  Collect: { label: LabelKeys.flux.collectionData, icon: 'edit_note' }, // 0
};
